/**
 * I/O layer for the NCEP GeoServer WMS radar source (RESEARCH.md opcja A /
 * DECISIONS.md D-005, primary as of M002). Pure computation lives in
 * frames.ts and mercator.ts; this module only does fetch/XML-parsing/URL
 * building. `loadFrame` (fetch/decode/pixel validation) is shared with the
 * ArcGIS fallback source and re-exported from radarApi.ts unchanged.
 */
import { boundsToMercatorBbox } from "./mercator";
import { CONUS_BOUNDS, RadarApiError, loadFrame } from "./radarApi";

export { loadFrame, RadarApiError, CONUS_BOUNDS };

export const WMS_BASE = "https://opengeo.ncep.noaa.gov/geoserver/conus/conus_bref_qcd/ows";

/**
 * Requested output width in px. Lowered from the ArcGIS source's 2000px
 * (DECISIONS.md D-005: ~48-frame window needs a smaller per-frame transfer,
 * measured ~60-90 KB at this width vs ~260 KB at 2000px).
 */
export const WMS_FRAME_WIDTH = 1280;

/**
 * Extract the layer's `<Dimension name="time">` comma-separated ISO8601
 * list from a layer-scoped GetCapabilities XML document (the endpoint is
 * scoped to a single layer, so there is exactly one such element) and
 * return epoch-ms, ascending. Pure string parsing — no DOMParser dependency,
 * so behaviour is identical in tests and the browser. Tolerant of
 * whitespace/newlines inside the list; returns [] for anything that does not
 * match (missing dimension, empty list, garbage content).
 */
export function parseTimeDimension(xml: string): number[] {
  const match = xml.match(/<Dimension\s+[^>]*name="time"[^>]*>([\s\S]*?)<\/Dimension>/i);
  if (!match) return [];
  const times = match[1]
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) => Date.parse(s))
    .filter((ms) => Number.isFinite(ms));
  return times.sort((a, b) => a - b);
}

/**
 * Fetch GetCapabilities (no-store: an advertised time list is only useful if
 * current) and return the advertised observation times, ascending.
 */
export async function fetchAdvertisedTimes(signal?: AbortSignal): Promise<number[]> {
  const url = `${WMS_BASE}?service=wms&version=1.3.0&request=GetCapabilities`;
  let res: Response;
  try {
    res = await fetch(url, { signal, cache: "no-store" });
  } catch (err) {
    throw new RadarApiError(
      `Nie udało się połączyć z serwisem NCEP WMS: ${(err as Error).message}`
    );
  }
  if (!res.ok) {
    throw new RadarApiError(`Serwis NCEP WMS zwrócił błąd HTTP ${res.status}`);
  }
  const xml = await res.text();
  const times = parseTimeDimension(xml);
  if (times.length === 0) {
    throw new RadarApiError("Odpowiedź serwisu NCEP WMS nie zawiera wymiaru czasu.");
  }
  return times;
}

/**
 * Build the GetMap URL for a single frame at the given observation time.
 * `timeMs` MUST be one of the values returned by `parseTimeDimension` /
 * `fetchAdvertisedTimes` — the server only has a rendered product for
 * timestamps it advertised itself. The advertised strings always carry a
 * zero millisecond component (e.g. `2026-07-19T09:04:09.000Z`), so
 * `Date#toISOString()` reproduces the exact advertised string byte-for-byte;
 * no separate ms→string table is needed to echo the time back verbatim.
 * `cacheBusterMs` (pass e.g. Date.now()) appends an inert `_cb` param, used
 * when retrying a previously deferred/invalid timestamp.
 */
export function buildWmsFrameUrl(
  timeMs: number,
  widthPx: number = WMS_FRAME_WIDTH,
  cacheBusterMs?: number
): string {
  const bbox = boundsToMercatorBbox(CONUS_BOUNDS);
  const aspect = (bbox.xmax - bbox.xmin) / (bbox.ymax - bbox.ymin);
  const height = Math.round(widthPx / aspect);
  const params = new URLSearchParams({
    service: "WMS",
    version: "1.3.0",
    request: "GetMap",
    layers: "conus_bref_qcd",
    crs: "EPSG:3857",
    bbox: `${bbox.xmin},${bbox.ymin},${bbox.xmax},${bbox.ymax}`,
    width: String(widthPx),
    height: String(height),
    format: "image/png",
    transparent: "true",
    time: new Date(Math.round(timeMs)).toISOString()
  });
  if (cacheBusterMs !== undefined) {
    params.set("_cb", String(cacheBusterMs));
  }
  return `${WMS_BASE}?${params.toString()}`;
}
